import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import useCmdKeys from "../../hooks/useCmdKeys";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

const rowSx = {
  display: "flex",
  alignItems: "center",
  // Whatever sits next to it should be what truncates, not the balance.
  flexShrink: 0
};

const compactSx = { ...rowSx, gap: "6px" };

const chipSx = {
  ...rowSx,
  gap: "8px",
  bgcolor: semanticColors.highlight.low,
  borderRadius: "40px",
  px: "16px",
  py: "8px"
};

const compactTextSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const chipTextSx = {
  ...typography.body.lSemibold,
  color: semanticColors.highlight.strong,
  textAlign: "center"
};

const symbolStyle = { width: 20, height: 20, objectFit: "contain" as const };

/**
 * How many Chave Móvel Digital signatures the professional has left.
 *
 * Renders nothing when the API reports no balance: an absent `cmdKeys` means
 * unknown, and a rendered zero would read as "none left".
 *
 * Two looks for two places:
 *  - `compact` sits beside the name in the profile menu, so it is just the
 *    symbol and the number, and carries a tooltip to explain the symbol.
 *  - `chip` heads the purchase screen, where there is room to spell the whole
 *    sentence out — which is also why it needs no tooltip.
 */
export default function CmdBalance({
  variant = "compact"
}: {
  variant?: "compact" | "chip";
}) {
  const { t } = useTranslation();
  const cmdKeys = useCmdKeys();

  if (cmdKeys === undefined) return null;

  const isChip = variant === "chip";
  const label = isChip
    ? cmdKeys === 0
      ? t("cmds.availableNone")
      : t("cmds.availableCount", { count: cmdKeys })
    : String(cmdKeys);

  const content = (
    <Box sx={isChip ? chipSx : compactSx}>
      <img src="/CMD_symbol.png" alt="" style={symbolStyle} />
      <Typography sx={isChip ? chipTextSx : compactTextSx}>{label}</Typography>
    </Box>
  );

  return isChip ? (
    content
  ) : (
    <Tooltip title={t("cmds.availableLabel")}>{content}</Tooltip>
  );
}
